'use client';

import { useState } from 'react';
import { CreditCard, Lightning, SpinnerGap } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/useToast';
import { routerTopupCheckout, routerTopupOneClick } from '@/apis/routerCredits';

const PRESETS = [25, 50, 100] as const;
const MIN_AMOUNT = 5;

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Add-credits dialog: the smooth money-in moment.
 *
 * Tries a one-click off-session charge against the saved card first (instant — no
 * redirect). If there's no card on file (or the charge needs interaction), it falls
 * back to hosted Stripe Checkout, which also saves the card for next time. On instant
 * success it reports the new balance up so the section can update without a round-trip.
 */
export function AddRouterCreditsDialog({
  open,
  onOpenChange,
  orgUuid,
  userUuid,
  onCredited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgUuid: string;
  userUuid: string;
  onCredited: (newBalanceUsd: number) => void;
}) {
  const toast = useToast();
  const [selected, setSelected] = useState<number>(50);
  const [custom, setCustom] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const customAmount = custom.trim() === '' ? null : Number(custom);
  const isCustom = custom.trim() !== '';
  const amount = isCustom ? customAmount : selected;
  const amountValid =
    amount !== null && Number.isFinite(amount) && amount >= MIN_AMOUNT;

  const handleAdd = async () => {
    if (!amountValid || amount === null) return;
    setSubmitting(true);
    // When we hand off to Stripe Checkout we navigate away, so the button must stay
    // disabled through the redirect — don't let `finally` re-enable it mid-navigation.
    let redirecting = false;
    try {
      const oneClick = await routerTopupOneClick(orgUuid, amount, userUuid);
      if (oneClick.Error) {
        toast({
          title: "Couldn't add credits",
          description: 'Please try again in a moment.',
          status: 'error',
        });
        return;
      }
      if (oneClick.NeedsCheckout) {
        // No saved card (or 3DS needed) → hand off to hosted Checkout.
        const checkout = await routerTopupCheckout(orgUuid, amount, userUuid);
        if (checkout.Error || !checkout.CheckoutUrl) {
          toast({
            title: "Couldn't open checkout",
            description: 'Please try again in a moment.',
            status: 'error',
          });
          return;
        }
        redirecting = true;
        window.location.href = checkout.CheckoutUrl;
        return; // navigating away; verify-on-return credits the wallet
      }
      // Instant success on the saved card.
      onCredited(oneClick.BalanceUsd);
      onOpenChange(false);
      toast({
        title: `${formatUsd(amount)} added`,
        description: 'Your Model Router balance is topped up.',
        status: 'success',
      });
    } catch {
      toast({
        title: "Couldn't add credits",
        description: 'Please check your connection and try again.',
        status: 'error',
      });
    } finally {
      if (!redirecting) setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="ds-text-title-sm-semibold text-ds-text-darker">
            Add Model Router credits
          </DialogTitle>
          <DialogDescription className="ds-text-body-sm-medium text-ds-text-subtle">
            5% of your router spend is the Entelligence fee, so a $100 top-up
            buys $95 of model usage. Unused balance never expires.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* Preset amounts */}
          <div className="grid grid-cols-3 gap-2">
            {PRESETS.map((preset) => {
              const active = !isCustom && selected === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    setSelected(preset);
                    setCustom('');
                  }}
                  aria-pressed={active}
                  className={`rounded-lg border px-3 py-3 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-brand-subtle/40 ${
                    active
                      ? 'border-ds-brand-subtle bg-ds-brand-lightest text-ds-text-darker'
                      : 'border-ds-border-subtlest bg-ds-surface-white text-ds-text-default hover:border-ds-border-subtler'
                  }`}
                >
                  <span className="ds-text-body-md-semibold tabular-nums">
                    ${preset}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Custom amount */}
          <div className="space-y-1.5">
            <label
              htmlFor="custom-credit-amount"
              className="ds-text-body-xs-medium text-ds-text-subtle"
            >
              Or enter a custom amount
            </label>
            <div className="relative">
              <span className="ds-text-body-md-semibold pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ds-text-subtle">
                $
              </span>
              <Input
                id="custom-credit-amount"
                type="number"
                inputMode="decimal"
                min={MIN_AMOUNT}
                step="1"
                placeholder="100"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="pl-7 tabular-nums"
              />
            </div>
            {isCustom && !amountValid && (
              <p className="ds-text-body-xs-medium text-ds-danger-default">
                Enter at least {formatUsd(MIN_AMOUNT)}.
              </p>
            )}
          </div>

          {/* Reassurance */}
          <div className="flex items-start gap-2 rounded-lg bg-ds-surface-off-white px-3 py-2.5">
            <CreditCard
              size={16}
              className="mt-0.5 shrink-0 text-ds-text-subtle"
            />
            <p className="ds-text-body-xs-medium text-ds-text-subtle">
              Paid instantly with your saved card, or we&apos;ll take you to
              secure Stripe checkout the first time.
            </p>
          </div>
        </div>

        <DialogFooter className="mt-2 gap-2 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleAdd}
            disabled={!amountValid || submitting}
            className="min-w-[7rem]"
          >
            {submitting ? (
              <SpinnerGap size={16} className="animate-spin" />
            ) : (
              <>
                <Lightning size={16} weight="fill" />
                Add{' '}
                {amount !== null && amountValid ? formatUsd(amount) : 'credits'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
